// Shared header 3-dot menu + Bulk-update-column dialog for ProjectView & ScopeView

import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MoreHorizontal, Pencil, Trash2, Plus, Columns3, Eraser } from "lucide-react";

export function HeaderMenu({ col, isAdmin, canBulk, onRename, onEditType, onDelete, onBulkUpdate, onAddColumn, onClearColumn, disabledEditTypeReason }) {
  const isCustom = !!col.isCustom;
  const deleteLabel = isCustom ? "Delete column" : "Hide column";
  const showEditType = isCustom;   // real Edit type dialog only for custom
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          data-testid={`col-menu-${col.key}`}
          title="Column options">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1">
        {isAdmin && onRename && (
          <button onClick={onRename} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-rename-btn-${col.key}`}>
            <Pencil className="h-3.5 w-3.5" /> Rename column
          </button>
        )}
        {isAdmin && showEditType && onEditType && (
          <button onClick={onEditType} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-edit-type-${col.key}`}>
            <Pencil className="h-3.5 w-3.5" /> Edit type / options
          </button>
        )}
        {isAdmin && !showEditType && (
          <button disabled title={disabledEditTypeReason || "Built-in column type is fixed"}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-slate-300 rounded cursor-not-allowed" data-testid={`col-edit-type-${col.key}-disabled`}>
            <Pencil className="h-3.5 w-3.5" /> Edit type / options
          </button>
        )}
        {canBulk && onBulkUpdate && (
          <button onClick={onBulkUpdate} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-bulk-update-${col.key}`}>
            <Columns3 className="h-3.5 w-3.5" /> Bulk update column
          </button>
        )}
        {canBulk && onClearColumn && (
          <button onClick={onClearColumn} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-clear-${col.key}`}>
            <Eraser className="h-3.5 w-3.5" /> Clear column
          </button>
        )}
        {isAdmin && onAddColumn && (
          <button onClick={onAddColumn} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-add-${col.key}`}>
            <Plus className="h-3.5 w-3.5" /> Add new column
          </button>
        )}
        {isAdmin && onDelete && (
          <button onClick={onDelete} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded" data-testid={`col-delete-btn-${col.key}`}>
            <Trash2 className="h-3.5 w-3.5" /> {deleteLabel}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function BulkUpdateColumnDialog({ open, onOpenChange, col, filteredCount, onApply }) {
  const [value, setValue] = useState("");
  const [scope, setScope] = useState("all");
  const [firstN, setFirstN] = useState(filteredCount || 10);
  useEffect(() => {
    if (open) { setValue(""); setScope("all"); setFirstN(filteredCount || 10); }
  }, [open, filteredCount]);
  if (!col) return null;
  const submit = async () => {
    const limit = scope === "all" ? filteredCount : Math.max(0, Math.min(filteredCount, Number(firstN) || 0));
    if (limit === 0) { onOpenChange(false); return; }
    let parsed = value;
    if (col.type === "number") parsed = value === "" ? "" : Number(value);
    if (col.type === "bool") parsed = value === "true" || value === "1" || value === true;
    if ((col.type === "user" || col.type === "member") && parsed === "") parsed = null;
    await onApply(parsed, limit);
    onOpenChange(false);
  };
  const renderInput = () => {
    if (col.type === "select" || col.type === "dropdown") {
      return (
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger className="w-full" data-testid="bulk-update-value"><SelectValue placeholder="Pick a value" /></SelectTrigger>
          <SelectContent>
            {(col.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === "user" || col.type === "member") {
      return (
        <Select value={value || "unassigned"} onValueChange={(v) => setValue(v === "unassigned" ? "" : v)}>
          <SelectTrigger className="w-full" data-testid="bulk-update-value"><SelectValue placeholder="Pick a user" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {(col.userList || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === "bool") {
      return (
        <Select value={String(value || "false")} onValueChange={setValue}>
          <SelectTrigger className="w-full" data-testid="bulk-update-value"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Checked</SelectItem>
            <SelectItem value="false">Unchecked</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    const inputType = col.type === "date" ? "date" : col.type === "number" ? "number" : "text";
    return <Input type={inputType} value={value} onChange={(e) => setValue(e.target.value)} placeholder={`New ${col.label} value…`} data-testid="bulk-update-value" />;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-update-dialog">
        <DialogHeader>
          <DialogTitle>Bulk update: <span className="text-purple-600">{col.label}</span></DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Overwrite this column's value across selected cells. Undo with Ctrl+Z.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>New value</Label>
            {renderInput()}
          </div>
          <div className="space-y-2">
            <Label>Apply to</Label>
            <RadioGroup value={scope} onValueChange={setScope}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="bu-all" data-testid="bulk-update-scope-all" />
                <Label htmlFor="bu-all" className="cursor-pointer font-normal">All {filteredCount} cell{filteredCount === 1 ? "" : "s"} in this column</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="first" id="bu-first" data-testid="bulk-update-scope-first" />
                <Label htmlFor="bu-first" className="cursor-pointer font-normal">First</Label>
                <Input type="number" value={firstN} onChange={(e) => setFirstN(e.target.value)} className="w-20 h-8" min={1} max={filteredCount} data-testid="bulk-update-first-n" onClick={() => setScope("first")} />
                <span className="text-xs text-slate-500">cell{Number(firstN) === 1 ? "" : "s"}</span>
              </div>
            </RadioGroup>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-update-cancel">Cancel</Button>
          <Button onClick={submit} data-testid="bulk-update-apply">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
